import { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  IconBell,
  IconChevron,
  IconDocs,
  IconLogout,
  IconMenu,
  IconSearch,
} from "../components/Icons";
import { useOutsideDismiss } from "../components/RequestRouting";
import { MODULES, searchIntents } from "../lib/modules";
import { rolesVisibleForEmail, type DemoRoleId } from "../lib/roles";
import { useAuth } from "../state/AuthContext";
import { useTasks } from "../hooks/useEntityList";

export function AppShell() {
  const {
    user,
    roleIds,
    moduleIds,
    roleSummary,
    homePath,
    effectiveEmail,
    emailOverride,
    setEmailOverride,
    toggleRoleId,
    signOut,
  } = useAuth();
  const navigate = useNavigate();
  const tasks = useTasks();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [bellOpen, setBellOpen] = useState(false);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [overrideDraft, setOverrideDraft] = useState("");
  const visibleRoles = useMemo(
    () => rolesVisibleForEmail(effectiveEmail),
    [effectiveEmail],
  );

  const closeRole = useCallback(() => setRoleMenuOpen(false), []);
  const closeBell = useCallback(() => setBellOpen(false), []);
  const closeSearch = useCallback(() => setSearch(""), []);
  const roleRef = useOutsideDismiss(roleMenuOpen, closeRole);
  const bellRef = useOutsideDismiss(bellOpen, closeBell);
  const searchRef = useOutsideDismiss(search.trim().length > 0, closeSearch);

  const allowed = useMemo(() => new Set(moduleIds), [moduleIds]);
  const allowedPaths = useMemo(
    () => new Set(MODULES.filter((m) => allowed.has(m.id)).map((m) => m.path)),
    [allowed],
  );

  const groups = useMemo(() => {
    const map = new Map<string, typeof MODULES>();
    for (const m of MODULES) {
      if (!allowed.has(m.id)) continue;
      const list = map.get(m.group) ?? [];
      list.push(m);
      map.set(m.group, list);
    }
    return [...map.entries()];
  }, [allowed]);

  useEffect(() => {
    const init: Record<string, boolean> = {};
    for (const [g] of groups) init[g] = true;
    setOpenGroups(init);
  }, [groups]);

  const intentResults = useMemo(() => searchIntents(search, allowedPaths), [search, allowedPaths]);

  return (
    <div className="min-h-screen flex bg-mist">
      <aside
        className={`${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 fixed lg:sticky top-0 z-40 left-0 h-screen ${
          collapsed ? "w-[84px]" : "w-[280px]"
        } bg-navy text-white flex flex-col transition-all duration-200 shrink-0`}
      >
        <div className="px-4 pt-5 pb-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal/30 flex items-center justify-center text-sm font-semibold">
              {(user?.name ?? "D").slice(0, 1).toUpperCase()}
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <div className="font-medium truncate">{user?.name ?? "Demo user"}</div>
                <div className="text-xs text-white/60 truncate">{user?.email}</div>
              </div>
            )}
          </div>
        </div>

        <div className="px-3 py-4">
          <img
            src="/govsuite-logo-on-dark.png"
            alt="GovSuite"
            className={`${collapsed ? "h-8 w-8 object-cover object-left" : "h-9 w-auto max-w-[200px] object-contain object-left"}`}
          />
        </div>

        <nav className="flex-1 overflow-y-auto px-2 pb-4 space-y-1">
          {groups.map(([group, mods]) => {
            const open = openGroups[group] ?? true;
            return (
              <div key={group}>
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2 text-[11px] uppercase tracking-wider text-white/50 hover:text-white/80"
                  onClick={() => setOpenGroups((s) => ({ ...s, [group]: !open }))}
                >
                  {!collapsed && <span>{group}</span>}
                  {!collapsed && (
                    <IconChevron
                      size={14}
                      className={`transition-transform ${open ? "rotate-90" : ""}`}
                    />
                  )}
                </button>
                {(collapsed || open) &&
                  mods.map((m) => (
                    <NavLink
                      key={m.id}
                      to={m.path}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-2 text-sm rounded-sm mb-0.5 ${
                          isActive ? "bg-white/15 text-white" : "text-white/90 hover:bg-white/10"
                        }`
                      }
                      title={m.label}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-teal shrink-0" />
                      {!collapsed && <span className="truncate">{m.label}</span>}
                    </NavLink>
                  ))}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-white/10 p-3 space-y-1 mt-auto">
          <NavLink
            to="/app/release-notes"
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `w-full flex items-center gap-2 px-3 py-2 text-sm ${
                isActive ? "bg-white/15 text-white" : "text-white/80 hover:bg-white/10"
              }`
            }
            title="Release notes"
          >
            <IconDocs size={18} />
            {!collapsed && <span>Release notes</span>}
          </NavLink>
        </div>
      </aside>

      {mobileOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          aria-label="Close menu"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div className="flex-1 min-w-0 min-h-screen flex flex-col">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-black/5 px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            className="p-2 hover:bg-mist lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
            title="Open menu"
          >
            <IconMenu />
          </button>
          <button
            type="button"
            className="p-2 hover:bg-mist hidden lg:inline-flex"
            onClick={() => setCollapsed((c) => !c)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <IconMenu />
          </button>

          <div className="relative flex-1 max-w-xl" ref={searchRef}>
            <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate" size={18} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="I need to…"
              className="w-full pl-10 pr-3 py-2 bg-mist border border-transparent focus:border-teal/30 focus:outline-none text-sm"
            />
            {intentResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-black/10 shadow-lg max-h-72 overflow-auto z-30">
                {intentResults.map((m) => (
                  <button
                    key={m.path + m.label}
                    type="button"
                    className="w-full text-left px-4 py-2.5 hover:bg-mist text-sm"
                    onClick={() => {
                      setSearch("");
                      navigate(m.path);
                    }}
                  >
                    <div className="font-medium text-navy">{m.label}</div>
                    <div className="text-xs text-slate">{m.hint}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <div className="hidden md:block relative" ref={roleRef}>
              <div className="text-[10px] uppercase tracking-wider text-slate mb-0.5">Role preview</div>
              <button
                type="button"
                onClick={() => setRoleMenuOpen((o) => !o)}
                className="text-sm font-medium text-navy bg-white border border-navy/20 px-2 py-1.5 min-w-[220px] text-left focus:outline-none focus:ring-2 focus:ring-teal/30"
              >
                {roleSummary}
                <span className="text-slate font-normal"> · {roleIds.length} selected</span>
              </button>
              {roleMenuOpen && (
                <div className="absolute right-0 mt-1 w-80 bg-white border border-black/10 shadow-xl z-40 p-2 max-h-96 overflow-auto">
                  <p className="px-2 py-1 text-xs text-slate mb-1">
                    Select multiple roles to mirror assigning several roles to one person.
                  </p>
                  <p className="px-2 py-1 text-[11px] text-slate border-b border-black/5 mb-1">
                    Session: {effectiveEmail || "—"}
                    {emailOverride ? " (override)" : ""}
                  </p>
                  {visibleRoles.map((r) => {
                    const checked = roleIds.includes(r.id);
                    return (
                      <label
                        key={r.id}
                        className="flex items-start gap-2 px-2 py-1.5 hover:bg-mist cursor-pointer text-sm"
                      >
                        <input
                          type="checkbox"
                          className="mt-0.5"
                          checked={checked}
                          onChange={() => toggleRoleId(r.id as DemoRoleId)}
                        />
                        <span className="font-medium text-navy">{r.label}</span>
                      </label>
                    );
                  })}
                  <div className="mt-2 px-2 pt-2 border-t border-black/5 space-y-1">
                    <div className="text-[10px] uppercase tracking-wider text-slate">
                      QA: @govsuite.com email override
                    </div>
                    <div className="flex gap-1">
                      <input
                        className="flex-1 border border-black/10 px-2 py-1 text-xs"
                        placeholder="support@govsuite.com"
                        value={overrideDraft}
                        onChange={(e) => setOverrideDraft(e.target.value)}
                      />
                      <button
                        type="button"
                        className="text-xs px-2 py-1 bg-navy text-white"
                        onClick={() => {
                          setEmailOverride(overrideDraft || null);
                          setOverrideDraft("");
                        }}
                      >
                        Set
                      </button>
                    </div>
                    {emailOverride && (
                      <button
                        type="button"
                        className="text-xs text-red-700 hover:underline"
                        onClick={() => setEmailOverride(null)}
                      >
                        Clear override
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    className="mt-2 w-full text-xs text-teal hover:underline px-2 py-1"
                    onClick={() => {
                      setRoleMenuOpen(false);
                      navigate(homePath);
                    }}
                  >
                    Go to home
                  </button>
                </div>
              )}
            </div>

            <div className="relative" ref={bellRef}>
              <button
                type="button"
                className="relative p-2 hover:bg-mist"
                onClick={() => setBellOpen((o) => !o)}
                aria-label="Notifications"
                title="Notifications"
              >
                <IconBell />
                {tasks.length > 0 && (
                  <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-teal text-[10px] text-white flex items-center justify-center">
                    {tasks.length}
                  </span>
                )}
              </button>
              {bellOpen && (
                <div className="absolute right-0 mt-1 w-80 bg-white border border-black/10 shadow-xl z-30">
                  <div className="px-3 py-2 border-b border-black/5 text-xs font-semibold uppercase tracking-wider text-slate">
                    Needs your attention
                  </div>
                  {tasks.length === 0 ? (
                    <div className="p-4 text-sm text-slate">No open tasks for selected roles.</div>
                  ) : (
                    tasks.map((t) => (
                      <div key={String(t.id)} className="px-3 py-2.5 border-b border-black/5 text-sm">
                        <div className="font-medium text-navy">{String(t.title)}</div>
                        <div className="text-xs text-slate">{String(t.queue)}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              className="p-2 hover:bg-mist text-slate"
              onClick={() => {
                signOut();
                navigate("/login");
              }}
              aria-label="Sign out"
              title="Sign out"
            >
              <IconLogout />
            </button>
          </div>
        </header>

        <div className="md:hidden px-4 py-2 bg-white border-b border-black/5">
          <div className="text-[10px] uppercase tracking-wider text-slate mb-1">Role preview</div>
          <div className="text-sm font-medium text-navy mb-2">{roleSummary}</div>
          <div className="grid grid-cols-1 gap-1 max-h-48 overflow-auto border border-navy/10 p-2">
            {visibleRoles.map((r) => (
              <label key={r.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={roleIds.includes(r.id)}
                  onChange={() => toggleRoleId(r.id)}
                />
                {r.label}
              </label>
            ))}
          </div>
        </div>

        <main className="flex-1 p-4 md:p-6 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
